import React, { useState, useEffect } from "react";
import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Label,
  Field,
  Radio,
  RadioGroup,
} from "@headlessui/react";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { ComponentI, ImageI } from "../../../../types";
import Card from "../../../../componentLibrary/Card";
import Input from "../../../../componentLibrary/Input";
import { useGetImages } from "../../../../api/queries";
import { ImageTypeEnum } from "../../../../types";
import Spinner from "../../../../componentLibrary/Spinner";
import Tooltip from "../../../../componentLibrary/Tooltip";

export default function CustomLinkContentCard({
  component,
  handleContentChange,
  provided,
}: {
  component: ComponentI;
  handleContentChange: (id: string, field: string, value: string) => void;
  provided: any; //eslint-disable-line @typescript-eslint/no-explicit-any
}) {
  const [query, setQuery] = useState("");
  const [filteredImages, setFilteredImages] = useState([]);
  const [imageType, setImageType] = useState(
    component.content.image_type || ImageTypeEnum.ICON,
  );
  const { data: images, isError, isLoading } = useGetImages(imageType);

  useEffect(() => {
    if (images) {
      const filteredImages1 =
        query === ""
          ? images
          : images.filter((image) =>
              image.name.toLowerCase().includes(query.toLowerCase()),
            );
      setFilteredImages(filteredImages1);
    }
  }, [images, query]);

  const setSelectedOption = (option) => {
    if (option) {
      const selectedImage = images.find((image) => image.src === option);
      if (selectedImage) {
        handleContentChange(component._id, "icon_url", selectedImage.src);
        handleContentChange(component._id, "icon_alt", selectedImage.alt);
      }
    }
  };

  const getImageFromURL = (url: string) => {
    const selectedImage = images?.find((image) => image.src === url);
    if (selectedImage) {
      return selectedImage;
    }
    return null;
  };

  const changeImageType = (option) => {
    setImageType(option);
    handleContentChange(component._id, "image_type", option);
    // Clear the current image when changing type
    handleContentChange(component._id, "icon_url", "");
    handleContentChange(component._id, "icon_alt", "");
  };

  if (isLoading) return <Spinner className="mx-auto" />;
  if (isError || !images) return <div>Error</div>;

  return (
    <Card>
      <div className="space-y-2">
        <Input
          value={component.content.button_text || ""}
          onChange={(e) =>
            handleContentChange(component._id, "button_text", e.target.value)
          }
          label="Button Text"
          placeholder="Click here"
          required
        />
        <div className="space-y-1">
          <div className="flex flex-row space-x-1 items-center">
            <div className="font-bold">URL Type</div>
            <Tooltip
              icon={<InformationCircleIcon className="h-4 w-4 text-gray-600" />}
              text="Local URLs start with / and link to pages within the app. External URLs should start with http:// or https://"
              position="right"
              isActive={true}
            />
          </div>
          <RadioGroup
            value={component.content.url_type || "external"}
            onChange={(value) =>
              handleContentChange(component._id, "url_type", value)
            }
            className="flex flex-row space-x-4"
          >
            <Field className="flex items-center gap-2">
              <Radio
                value="external"
                className="group flex size-5 items-center justify-center rounded-full border bg-white data-[checked]:bg-blue-400"
              >
                <span className="invisible size-2 rounded-full bg-white group-data-[checked]:visible" />
              </Radio>
              <Label>External URL</Label>
            </Field>
            <Field className="flex items-center gap-2">
              <Radio
                value="local"
                className="group flex size-5 items-center justify-center rounded-full border bg-white data-[checked]:bg-blue-400"
              >
                <span className="invisible size-2 rounded-full bg-white group-data-[checked]:visible" />
              </Radio>
              <Label>Local URL</Label>
            </Field>
          </RadioGroup>
        </div>
        <Input
          value={component.content.url || ""}
          onChange={(e) =>
            handleContentChange(component._id, "url", e.target.value)
          }
          label="URL"
          placeholder={
            component.content.url_type === "local"
              ? "/explore"
              : "https://example.com"
          }
          required
        />
        <div className="space-y-1">
          <RadioGroup
            value={imageType}
            onChange={changeImageType}
            className="flex flex-row space-x-4"
          >
            <Field className="flex items-center gap-2">
              <Radio
                value={ImageTypeEnum.ICON}
                className="group flex size-5 items-center justify-center rounded-full border bg-white data-[checked]:bg-blue-400"
              >
                <span className="invisible size-2 rounded-full bg-white group-data-[checked]:visible" />
              </Radio>
              <Label>Icon</Label>
            </Field>
            <Field className="flex items-center gap-2">
              <Radio
                value={ImageTypeEnum.ILLUSTRATION}
                className="group flex size-5 items-center justify-center rounded-full border bg-white data-[checked]:bg-blue-400"
              >
                <span className="invisible size-2 rounded-full bg-white group-data-[checked]:visible" />
              </Radio>
              <Label>Illustration</Label>
            </Field>
          </RadioGroup>
        </div>
        <div className="flex flex-row justify-between items-center">
          <div
            className="w-2/3 relative"
            data-rbd-drag-handle-context-id={
              provided.dragHandleProps?.["data-rbd-drag-handle-context-id"]
            }
            data-rbd-drag-handle-draggable-id="gibberish"
          >
            <Combobox
              immediate
              value={getImageFromURL(component.content?.icon_url)}
              onChange={setSelectedOption}
            >
              <Label className="block text-sm font-medium text-gray-700">
                {imageType} (Optional)
              </Label>
              <ComboboxInput
                aria-label={imageType}
                displayValue={(image: ImageI | null) => image?.name || ""}
                onChange={(event) => setQuery(event.target.value)}
                autoComplete="off"
                placeholder={`Select an ${imageType.toLowerCase()}`}
                className="w-full px-4 py-2 border border-[#0F172A] rounded-md focus:outline-none focus:border-[#00A3E0] disabled:cursor-not-allowed"
              />
              <ComboboxOptions className="absolute mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm z-40">
                {filteredImages.length === 0 && query !== "" ? (
                  <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                    No results found
                  </div>
                ) : (
                  filteredImages.map((option) => (
                    <ComboboxOption
                      key={option._id}
                      value={option.src}
                      className={({ active }) =>
                        `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? "bg-blue-100" : ""}`
                      }
                    >
                      {({ selected }) => (
                        <div className="flex items-center">
                          <img
                            src={option.src}
                            alt={option.alt}
                            className="h-6 w-6 flex-shrink-0"
                          />
                          <span
                            className={`ml-3 block truncate ${selected ? "font-medium" : "font-normal"}`}
                          >
                            {option.name}
                          </span>
                        </div>
                      )}
                    </ComboboxOption>
                  ))
                )}
              </ComboboxOptions>
            </Combobox>
          </div>
          {component.content.icon_url ? (
            <img
              src={component.content.icon_url}
              alt={component.content.icon_alt}
              className="h-16 w-16 bg-gray-100 border-2 border-dashed"
            />
          ) : (
            <div className="h-16 w-16 bg-gray-100 border-2 border-dashed"></div>
          )}
        </div>
      </div>
    </Card>
  );
}
